import 'bootstrap/dist/css/bootstrap.css';
import { useState } from 'react';
import Spinner from '../../shared/spinner';
import ScrollToTopButton from '../../shared/scrollToTopButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import InputMask from 'react-input-mask';
import CustomAlert from '../../shared/customAlert';
import ProcessadoraCartaoService from '../../services/processadoraCartaoService'; 

// Definição da interface para os dados de saída (retorno 200)
interface ConsultaLimiteProcessadoraCartao {
  returnCode: number;
  returnDescription: string;
  availableLimit: number; // Limite de segurança disponível
  availableCreditLimitUsed: number; // Limite de crédito disponível utilizado
  cardHolderType: 'Holder' | 'Additional' | 'Virtual'; // Tipo do titular
  cardType: string; // Tipo do cartão
  creditLimit: number; // Limite de crédito da conta
  creditLimitUsed: number;
  globalLimitUsed: number; // Valor total do limite de segurança distribuído entre os portadores
  // Outros campos podem existir, mas estes são os principais documentados
}

// Definição da interface para o retorno de erro (ex: 404, 422)
interface LimiteError {
  returnCode: string; 
  returnDescription: string;
}

function ConsultaLimiteCartao() {
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);

  // Campo de entrada
  const [numeroProposta, setNumeroProposta] = useState(''); 

  // Estado para armazenar o resultado da consulta
  const [resultadoSucesso, setResultadoSucesso] = useState<ConsultaLimiteProcessadoraCartao | null>(null);
  const [resultadoErro, setResultadoErro] = useState<LimiteError | null>(null);
  const [exibeResultado, setExibeResultado] = useState(false);
  
  const processadoraCartaoService = new ProcessadoraCartaoService();

  // Função para realizar a chamada de API
  const consultarLimites = async () => {
    // 1. Validações de entrada (usando numeroProposta)
    if (numeroProposta.length === 0) {
      setAlert({ message: 'O Número da Proposta não pode estar vazio.', type: 'warning' });
      setExibeResultado(false);
      return;
    }

    setLoading(true);
    setResultadoSucesso(null);
    setResultadoErro(null);

    // Corpo da requisição (usando numeroProposta)
    const requestBody = { numeroProposta: numeroProposta };

    try {
      const response = await processadoraCartaoService.consultarLimiteConta(requestBody);
      
      const data: ConsultaLimiteProcessadoraCartao = response.data;

      setResultadoSucesso(data);
      setAlert({ message: 'Consulta de limites efetuada com sucesso!', type: 'success' });

    } catch (err: any) {
      const errorData = err.response?.data;
      const statusCode = err.response?.status?.toString() || '500';
      
      const returnCode = errorData?.returnCode?.toString() || statusCode;
      const returnDescription = errorData?.returnDescription || 'Erro desconhecido ao consultar limites.';

      setResultadoErro({ returnCode: returnCode, returnDescription: returnDescription });
      setAlert({ message: `Erro ${returnCode}: ${returnDescription}`, type: 'error' });
      console.error(err);
      
    } finally {
      setExibeResultado(true);
      setLoading(false);
    }
  };

  // Helper para formatar moeda
  const formatCurrency = (value: number | null | undefined) => {
    const numericValue = value ?? 0; // Se for null ou undefined, usa 0
    return `R$ ${numericValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }


  return (
    <>
      {alert && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}
      <div className="position-relative m-md-3">
        {loading ? <Spinner loading={loading} /> : (
          <>
            <div className="card" style={{ marginTop: '25px' }}>
              <div className="card-body">
                <div className="card-header">
                  <strong>Consulta de Limites de Cartão</strong>
                </div>

                {/* --- Campos de Entrada --- */}
                <div className="row" style={{ marginTop: '15px' }}>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Número da Proposta <span style={{ color: 'red' }}>*</span></label> 
                      <InputMask
                        mask="9999"
                        type="text"
                        className="form-control form-control-sm brds"
                        style={{ color: 'Black', backgroundColor: 'White' }}
                        value={numeroProposta}
                        onChange={(e) => setNumeroProposta(e.target.value.replace(/_/g, ""))} 
                        placeholder="Ex: 1234"
                      />
                    </div>
                  </div>
                </div>

                {/* --- Botão de Consulta --- */}
                <div className="row" style={{ marginTop: '15px' }}>
                  <div className="col-md-3">
                    <button className="input-group-btn" id="App-button-cor-principal"
                      style={{
                        width: '80%', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '50px', minWidth: '50px'
                      }} onClick={consultarLimites}>
                      <a>
                        <FontAwesomeIcon icon={faSearch} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                      </a>
                      <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Consultar Limites</strong>
                    </button>
                  </div>
                </div>

                {/* --- Exibição de Resultado de Sucesso (200) --- */}
                {exibeResultado && resultadoSucesso && (
                  <>
                    <hr style={{marginTop: '20px', marginBottom: '20px'}} />
                    <h4><strong>Dados de Limite (Sucesso - 200)</strong></h4>
                    <div className="row" style={{ marginTop: '10px' }}>
                        {/* Linha 1: Limites */}
                        <div className="col-md-3">
                            <label>Limite de Crédito da Conta:</label>
                            <input type="text" readOnly className="form-control form-control-sm brds" value={formatCurrency(resultadoSucesso.creditLimit)} style={{ backgroundColor: 'LightGrey' }} />
                        </div>
                        <div className="col-md-3">
                            <label>Limite de Segurança Disponível:</label>
                            <input type="text" readOnly className="form-control form-control-sm brds" value={formatCurrency(resultadoSucesso.availableLimit)} style={{ backgroundColor: 'LightGrey' }} />
                        </div>
                        <div className="col-md-3">
                            <label>Limite Global Utilizado:</label>
                            <input type="text" readOnly className="form-control form-control-sm brds" value={formatCurrency(resultadoSucesso.globalLimitUsed)} style={{ backgroundColor: 'LightGrey' }} />
                        </div>
                        <div className="col-md-3">
                            <label>Crédito Utilizado:</label>
                            <input type="text" readOnly className="form-control form-control-sm brds" value={formatCurrency(resultadoSucesso.creditLimitUsed)} style={{ backgroundColor: 'LightGrey' }} />
                        </div>
                    </div>
                    <div className="row" style={{ marginTop: '10px' }}>
                        {/* Linha 2: Tipos de Cartão/Titular */}
                        <div className="col-md-3">
                            <label>Tipo do Cartão:</label>
                            <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoSucesso.cardType} style={{ backgroundColor: 'LightGrey' }} />
                        </div>
                        <div className="col-md-3">
                            <label>Tipo do Titular:</label>
                            <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoSucesso.cardHolderType} style={{ backgroundColor: 'LightGrey' }} />
                        </div>
                        <div className="col-md-6">
                            <label>Mensagem de Retorno:</label>
                            <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoSucesso.returnDescription} style={{ backgroundColor: 'LightGrey' }} />
                        </div>
                    </div>
                  </>
                )}

                {/* --- Exibição de Resultado de Erro (404/422) --- */}
                {exibeResultado && resultadoErro && (
                  <>
                    <hr style={{marginTop: '20px', marginBottom: '20px'}} />
                    <h4><strong>Status da Consulta (Erro - {resultadoErro.returnCode})</strong></h4>
                    <div className="row" style={{ marginTop: '10px' }}>
                      <div className="col-md-2">
                        <label>Código de Retorno:</label>
                        <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoErro.returnCode} style={{ backgroundColor: '#f8d7da', color: '#721c24' }} />
                      </div>
                      <div className="col-md-10">
                        <label>Descrição:</label>
                        <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoErro.returnDescription} style={{ backgroundColor: '#f8d7da', color: '#721c24' }} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div >
      <ScrollToTopButton />
    </>
  );
}

export default ConsultaLimiteCartao;